import * as React from 'react';
import { centeredMainGridClasses } from '@/styles/grid';
import { getOr } from '@/utils/options';
import { Paragraph } from 'components/src/typography';
import Tab, { Tabs, TabPanel } from 'components/src/tab';
import LargeAddOn from '../LargeAddOn';
import {
  TabGroupFieldsFragment,
  TabFieldsFragment,
  PackageCardFieldsFragment,
  LargeCardFieldsFragment,
  BentoBoxFieldsFragment,
  SmallCardFieldsFragment,
  Cards2f1FieldsFragment,
  SubProductCardStoreFieldsFragment,
} from 'types/generated';
import PackageCard from '../PackageCard';
import { Maybe } from 'graphql/jsutils/Maybe';
import Card2Fyrir1 from '../2fyrir1/card';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS, INLINES } from '@contentful/rich-text-types';
import { formatLink } from '@/utils/utils';
import { PdfIcon } from 'components/src/svg';
import Link from 'next/link';
import RichText from '@/components/RichTextSection/RichText';
import clsx from 'clsx';

type CardCollection = Array<
  | LargeCardFieldsFragment
  | SmallCardFieldsFragment
  | PackageCardFieldsFragment
  | Cards2f1FieldsFragment
  | BentoBoxFieldsFragment
  | SubProductCardStoreFieldsFragment
  | null
>;

type Props = {
  data: TabGroupFieldsFragment;
};

const renderAllCards = (items: CardCollection, disclaimer?: Maybe<string>) => {
  return (
    <>
      <div className={`${centeredMainGridClasses} mt-40 gap-y-16`}>
        {items.map((item) => {
          if (item?.__typename === 'PackageCard') {
            return <PackageCard key={item.id} data={item} hasDisclaimer={Boolean(disclaimer)} />;
          } else if (item?.__typename === 'Card2F1') {
            return <Card2Fyrir1 key={item.id} data={item} />;
          } else {
            return;
          }
        })}
      </div>
      {disclaimer && (
        <div className="mt-24 rounded-28 bg-white-15  p-8 lg:p-16">
          <Paragraph classes="inline text-red-100" variant="small">
            {'*'}
          </Paragraph>
          <Paragraph variant="small" classes="inline text-black-100">
            {disclaimer}
          </Paragraph>
        </div>
      )}
    </>
  );
};

const renderShowAllContent = ({ data }: Props) => {
  const tabs = data.tabsCollection?.items;

  if (tabs === null || tabs === undefined) {
    return;
  }

  const allItems = tabs.reduce<CardCollection>((acc, curr) => {
    if (curr?.content?.__typename === 'CardCollection') {
      const items = curr.content.cardCollection?.items;
      acc = acc.concat(items ?? []);
    }
    return acc;
  }, []);

  return renderAllCards(allItems);
};

const renderTabContent = (tab?: Maybe<TabFieldsFragment>) => {
  if (tab === null || tab === undefined) {
    return;
  } else if (tab.content?.__typename === 'CardCollection') {
    return (
      tab?.content?.cardCollection?.items && renderAllCards(tab.content.cardCollection?.items, tab.content.disclaimer)
    );
  } else if (tab.content?.__typename === 'LargeAddOn') {
    return <LargeAddOn data={tab.content} />;
  } else if (tab.content?.__typename === 'Table') {
    const json = tab.content.table?.json;
    const tableLinks = tab.content.table?.links;
    const headerColor = tab.content.headerColor;

    console.log('tab content', tab.content);

    return documentToReactComponents(json as Document, {
      renderNode: {
        [BLOCKS.TABLE]: (_node, children) => {
          if (!Array.isArray(children)) return;

          const numberOfColumns = children[0]?.props?.children?.length || 0;

          const [headerRow, ...bodyRows] = children; // split header from body

          return (
            <div className="grid-span-full grid rounded-16 bg-white-100">
              <div
                style={{
                  gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
                }}
                className="content-table-container grid justify-between overflow-y-scroll"
              >
                {/* wrap header */}
                <div className="content-header-row contents">{headerRow?.props?.children}</div>
                {/* wrap each body row */}
                {bodyRows.map((row, index) => (
                  <div key={index} className="content-body-row contents">
                    {row?.props?.children}
                  </div>
                ))}
              </div>
            </div>
          );
        },
        [BLOCKS.TABLE_ROW]: (_node, children) => {
          if (!Array.isArray(children)) return;
          return <>{children}</>;
        },
        [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
          <div
            className={`content-header-cell-container flex bg-${headerColor} px-24 py-24 text-md font-bold`}
            style={{
              color: headerColor === 'red-100' ? 'white' : '',
            }}
          >
            {children}
          </div>
        ),
        [BLOCKS.TABLE_CELL]: (_node, children) => <div className="flex px-24 py-24 text-md">{children}</div>,
        [INLINES.HYPERLINK]: (node, children) => {
          return (
            <Link
              href={formatLink(node.data.uri)}
              className="text-md text-red-100 hover:underline hover:decoration-red-100"
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </Link>
          );
        },
        [BLOCKS.PARAGRAPH]: (_node, children) => (
          <div style={{ marginTop: '10px', marginBottom: '10px' }}>{children}</div>
        ),
        [INLINES.ASSET_HYPERLINK]: (node) => {
          const id = node.data.target.sys?.id;
          const links = tableLinks?.assets.hyperlink;
          let url = '';
          links &&
            links.forEach((link) => {
              if (link?.sys.id === id) {
                url = getOr(link?.url, '');
              }
            });
          return (
            <a href={url} download>
              <PdfIcon />
            </a>
          );
        },
      },
    });
  } else if (tab.content?.__typename === 'ContentTypeRichText' && tab.content.richText) {
    return (
      <RichText
        data={tab.content.richText || {}}
        isFilloutStandardEmbed={tab.content.isFilloutStandardEmbed || false}
        textAlignment={tab.content.textAlignment}
      />
    );
  }
  return;
};

const TabsSection = ({ data }: Props) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const showAllTitle = data.showAllTitle;
  const tabs = data.tabsCollection?.items;
  const indexOffset = showAllTitle ? 1 : 0;

  const handleChangeTab = (newTabIndex: number) => {
    setSelectedIndex(newTabIndex);
  };

  return (
    <>
      <Tabs classes={clsx({ hidden: tabs?.length && tabs.length <= 1 })}>
        {showAllTitle && (
          <Tab index={0} label={showAllTitle} selected={selectedIndex === 0} onClick={handleChangeTab} />
        )}
        {/* This condition is to make sure we don't show the tab selector bar when there's only 1 tab to select.
              In that case we still display the tab content for that single tab, but just hide the tab selector. */}
        {tabs &&
          (showAllTitle || tabs.length > 1) &&
          tabs?.map((tab, index) => {
            return (
              <Tab
                key={`${tab?.title}-${index}`}
                index={index + indexOffset}
                buttonlayoutId={`tab-section-${data.title}`}
                label={getOr(tab?.title, '')}
                selected={selectedIndex === index + indexOffset}
                onClick={handleChangeTab}
              />
            );
          })}
      </Tabs>
      {showAllTitle && (
        <TabPanel value={selectedIndex} index={0}>
          {renderShowAllContent({ data })}
        </TabPanel>
      )}
      {tabs?.map((tab, index) => {
        return (
          <TabPanel key={`${tab?.title}-${index}`} value={selectedIndex} index={index + indexOffset}>
            {renderTabContent(tab)}
          </TabPanel>
        );
      })}
    </>
  );
};
export default TabsSection;
export { renderAllCards, renderTabContent };
